import type { ForwardRefExoticComponent, MouseEventHandler, ReactNode, RefAttributes } from 'react';
import { Children, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useModifiers } from '../../styles/hooks/useModifiers';

import { Dots } from './dots/Dots';

import styles from './Swiper.module.css';

export interface SwiperProps {
  children: ReactNode;
  className?: string;
  onActiveChange?: (index: number) => void;
  dots?: boolean;
  slideContainerClassName?: string;
}

export interface ISwiperHandle {
  goTo: (index: SlideIndex) => void;
}

type SlideIndex = number | 'next' | 'previous';

interface IMoveObject {
  scrollLeft: number;
  isStartMove: boolean;
  isStopClick: boolean;
  startX: number;
  moveLength: number;
}

const THRESHOLD_MOVE = 10;
const THRESHOLD_UP = 50;

export const Swiper: ForwardRefExoticComponent<SwiperProps & RefAttributes<ISwiperHandle>> = forwardRef<
  ISwiperHandle,
  SwiperProps
>(function SwipeScroll(
  { children, className = '', onActiveChange, dots = false, slideContainerClassName = '' }: SwiperProps,
  ref
) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const activeIndexRef = useRef(0);

  const [moveObject, setMoveObject] = useState<IMoveObject>({
    scrollLeft: 0,
    isStartMove: false,
    isStopClick: false,
    startX: 0,
    moveLength: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const mods = useModifiers(
    styles,
    'swiper__container',
    {},
    {
      withBaseClass: true,
    }
  );

  const scrollToIndex = useCallback(
    (index: number) => {
      if (!containerRef.current) return;
      const swiperElement = containerRef.current;
      swiperElement.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
    },
    [slideWidth]
  );

  const goTo = useCallback(
    (index: SlideIndex): void => {
      const activeIndex = activeIndexRef.current;

      if (index === 'next' || index === 'previous') {
        if (index === 'next' && activeIndex < slideCount - 1) {
          const nextIndex = activeIndex + 1;
          scrollToIndex(nextIndex);
        }
        if (index === 'previous' && activeIndex > 0) {
          const previousIndex = activeIndex - 1;
          scrollToIndex(previousIndex);
        }
      } else if (index >= 0 && index < slideCount) {
        scrollToIndex(index);
      }
    },
    [scrollToIndex, slideCount]
  );

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (moveObject.isStopClick) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    const container = containerRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    container.style.scrollSnapType = 'none';

    setMoveObject((prev) => ({
      ...prev,
      scrollLeft,
      isStartMove: true,
      startX: event.clientX,
      moveLength: 0,
    }));
  };

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!moveObject.isStartMove) return;

    const container = containerRef.current;
    if (!container) return;

    const moveLength = moveObject.startX - event.clientX;
    const nextScrollPosition = moveObject.scrollLeft + moveLength;

    if (
      THRESHOLD_MOVE > Math.abs(moveLength) ||
      nextScrollPosition < 0 ||
      (moveLength > 0 && activeIndex === slideCount - 1)
    ) {
      return;
    }

    container.scrollTo({ left: nextScrollPosition, behavior: 'auto' });

    setMoveObject((prev) => ({
      ...prev,
      isStopClick: true,
      moveLength,
    }));
  };

  const handleMouseUp: MouseEventHandler<HTMLDivElement> = () => {
    const container = containerRef.current;
    if (!container || !moveObject.isStartMove) return;
    if (Math.abs(moveObject.moveLength) < THRESHOLD_UP) {
      goTo(activeIndex);
    } else if (moveObject.moveLength > 0 + THRESHOLD_MOVE) {
      goTo('next');
    } else if (moveObject.moveLength < 0 + THRESHOLD_MOVE) {
      goTo('previous');
    }

    timerRef.current = setTimeout(() => {
      container.style.scrollSnapType = 'x mandatory';
    }, 800);

    setMoveObject({
      scrollLeft: 0,
      isStartMove: false,
      isStopClick: false,
      startX: 0,
      moveLength: 0,
    });
  };

  const newChildren = useMemo(() => {
    return Children.map(children, (child, index) => (
      <div data-id={index} className={styles.swiper__item}>
        {child}
      </div>
    ));
  }, [children]);

  useImperativeHandle(
    ref,
    () => ({
      goTo(index: SlideIndex): void {
        goTo(index);
      },
    }),
    [goTo]
  );

  useEffect(() => {
    if (onActiveChange) {
      onActiveChange(activeIndex);
    }
  }, [activeIndex, onActiveChange]);

  const setInitial = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;

    setSlideWidth(containerWidth);
    setSlideCount(container.children.length);
    goTo(activeIndexRef.current);
  }, [goTo]);

  useEffect(() => {
    setInitial();
    const handleWindowResize = setInitial;
    window.addEventListener('resize', handleWindowResize, { passive: true });
    return (): void => {
      window.removeEventListener('resize', handleWindowResize);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [setInitial]);

  const callBackObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('data-id');
        setActiveIndex(Number(id));
      }
    });
  }, []);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(callBackObserver, {
      root: container,
      threshold: 0.96, //set to 0.96 to prevent flickering
      rootMargin: '0px',
    });

    container.childNodes.forEach((child) => {
      observer.observe(child as HTMLElement);
    });

    return (): void => {
      observer.disconnect();
    };
  }, [callBackObserver]);

  return (
    <div
      className={`${className} ${styles.swiper__wrapper}`}
      onClickCapture={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
    >
      <div className={`${mods} ${slideContainerClassName}`} ref={containerRef}>
        {newChildren}
      </div>
      <Dots dots={dots} activeIndex={activeIndex} goTo={goTo} slideCount={slideCount} />
    </div>
  );
});
